/**
 * KAN-847: "Входящие звонки" в карточке менеджера. Звонок клиента на номер
 * кабинета сначала идёт своему менеджеру клиента, затем линии 1, 2, 3
 * (переключатель "Входящие звонки менеджерам по линиям" в голосовых
 * настройках кабинета). Правит только владелец, сохраняется сразу:
 * PATCH /managers/{id}/ {call_line}; остальным значение видно текстом.
 */
import { useId } from 'react';
import { NativeSelect } from '@/components/ui/native-select';
import { useManagerMutations } from '@/hooks/useManagers';
import { extractErrorMessage } from '@/lib/extractErrorMessage';
import type { ManagerCallLine } from '@/types/manager';

export const CALL_LINE_LABEL = 'Входящие звонки';
export const CALL_LINE_HINT = 'Сначала звонит свой менеджер клиента, затем линия 1, 2, 3';
const NONE = '';

const CALL_LINE_OPTIONS = [
  { value: '1', label: 'Линия 1' },
  { value: '2', label: 'Линия 2' },
  { value: '3', label: 'Линия 3' },
  { value: NONE, label: 'Не принимает' },
];

function callLineText(line: ManagerCallLine | null | undefined): string {
  return line ? `Линия ${line}` : 'Не принимает';
}

interface Props {
  managerId: string;
  callLine: ManagerCallLine | null | undefined;
  canEdit: boolean;
}

export function ManagerCallLineSelect({ managerId, callLine, canEdit }: Props): JSX.Element {
  const { update } = useManagerMutations();
  const id = useId();
  const pending = update.isPending ? update.variables?.data.call_line : undefined;
  const shown = pending !== undefined ? pending : callLine;

  return (
    <div className="text-sm" data-testid="manager-call-line">
      <label htmlFor={id} className="font-medium text-gray-900">
        {CALL_LINE_LABEL}
      </label>
      {canEdit ? (
        <div className="mt-1 w-48">
          <NativeSelect
            id={id}
            value={shown ? String(shown) : NONE}
            disabled={update.isPending}
            options={CALL_LINE_OPTIONS}
            aria-describedby={`${id}-hint`}
            onChange={(event) => {
              const raw = event.target.value;
              const line = raw === NONE ? null : (Number(raw) as ManagerCallLine);
              update.mutate({ id: managerId, data: { call_line: line } });
            }}
          />
        </div>
      ) : (
        <p className="mt-1 text-gray-700">{callLineText(callLine)}</p>
      )}
      <p id={`${id}-hint`} className="text-xs text-gray-500">
        {CALL_LINE_HINT}
      </p>
      {update.error && (
        <p className="mt-1 text-xs text-red-700" data-testid="manager-call-line-error">
          {extractErrorMessage(update.error, 'Не удалось сохранить')}
        </p>
      )}
    </div>
  );
}
